/**
 * Client for the home page's "What's new" carousel links (epic
 * 2026-10-09-whats-new-v2-links-favicon): `GET /api/web/v2/whats-new/links`.
 *
 * One call per session (cached by the carousel's query), no parameters: the
 * API answers, for the signed-in user, the most specific target of each
 * slide's link, or `null` per key when no level of its rule finds one. The
 * API never returns a URL; the web builds the hrefs (`whatsNew.ts`).
 *
 * Types come from `@nu-assist/contracts` (generated from the API's OpenAPI).
 * Auth: makeAuthedFetch (401 → refresh → retry once), as every web client.
 */
import type { WhatsNewLinks } from "@nu-assist/contracts";
import { makeAuthedFetch } from "@nu-assist/shared-fe";

import type { FetchLike } from "./imports.client";

export interface WhatsNewClientConfig {
  apiBase: string;
  fetchImpl?: FetchLike;
}

export class WhatsNewApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string
  ) {
    super(`What's new request failed (${status}) for ${url}`);
    this.name = "WhatsNewApiError";
    Object.setPrototypeOf(this, WhatsNewApiError.prototype);
  }
}

export function createWhatsNewClient(cfg: WhatsNewClientConfig) {
  const authedFetch: FetchLike =
    cfg.fetchImpl ??
    makeAuthedFetch({
      apiBase: cfg.apiBase,
      onSessionExpired: () => window.location.assign("/login"),
    });

  return {
    /** The caller's most specific target for each slide's link. */
    async getLinks(): Promise<WhatsNewLinks> {
      const url = `${cfg.apiBase}/api/web/v2/whats-new/links`;
      const res = await authedFetch(url, { method: "GET", credentials: "include" });
      if (!res.ok) throw new WhatsNewApiError(res.status, url);
      return (await res.json()) as WhatsNewLinks;
    },
  };
}

export type WhatsNewClient = ReturnType<typeof createWhatsNewClient>;
